import moment from "moment";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell, Tooltip, Customized } from 'recharts';

import BarCharts from "./bar-charts";
import { TestTypeService } from "../../../../utils/test-type-registry";
import { metaForCompetency, sortResultsByCatalog } from "../../../../utils/competency-catalog";
const defaultDate = '1810-01-01T00:00:00.000Z'

// Qué tests se grafican: el unificado trae DISC y competencias juntos; el de
// competencias usa el DISC del último año (si lo hay). Misma regla de siempre.
const resolveChartTests = (test) => {
  let discTest = test;
  let competencyTest = test;
  if (TestTypeService.isTestCategory(test, 'unified')) {
    discTest = test.results?.resultTestDisc ? { results: test.results.resultTestDisc } : null;
    competencyTest = test;
  } else if (TestTypeService.isTestCategory(test, 'competency')) {
    discTest = test.testDisc || null;
  } else {
    competencyTest = null;
  }
  return { discTest, competencyTest };
};

// Datos del colaborador (mismos campos que antes).
export const CollaboratorInfo = ({ collaborator }) => (
  <dl className='gm-collab-info'>
    <div><dt>Nombre</dt><dd>{`${collaborator?.name || ''} ${collaborator?.surname || ''}`}</dd></div>
    <div><dt>Rut</dt><dd>{collaborator?.rut || ''}</dd></div>
    <div><dt>Correo</dt><dd>{collaborator?.email || ''}</dd></div>
    <div><dt>Edad</dt><dd>{collaborator?.startDate === defaultDate ? 'No informada' : `${moment().diff(collaborator?.birthDate || new Date(), 'years')} años`}</dd></div>
    {/* La empresa propia del colaborador; si no se informó, la del cliente. */}
    <div><dt>Empresa</dt><dd>{collaborator?.company || collaborator?.clientName || ''}</dd></div>
    <div><dt>Área de trabajo</dt><dd>{collaborator?.area || 'No informada'}</dd></div>
    <div><dt>Cargo</dt><dd>{collaborator?.position || ''}</dd></div>
    <div><dt>Inicio en la empresa</dt><dd>{ collaborator?.startDate === defaultDate ? 'No informada' : moment(collaborator?.startDate || new Date()).format('DD/MM/YYYY')}</dd></div>
  </dl>
);

// Gráficos del test seleccionado (DISC y competencias), con "Ver respuestas".
export const CollaboratorResults = ({ test, onSeeAnswersClick, mustBeShowAnswersAction }) => {
  const { discTest, competencyTest } = resolveChartTests(test);
  const showDiscTestBarCharts = !!discTest?.results;
  const showCompetencyTestBarCharts = competencyTest && competencyTest.results;
  if (!showDiscTestBarCharts && !showCompetencyTestBarCharts) return null;
  return (
    <div className='gm-folder-content-container-v2'>
      <div className="gm-folder-section-container">
        {
          showDiscTestBarCharts &&
            <div className="gm-folder-section">
              <div className="gm-folder-section-header">
                <p>RESULTADOS TEST DISC</p>
              </div>
              <BarCharts data={discTest || []} />
            </div>
        }
        {
          showCompetencyTestBarCharts &&
            <div className="gm-folder-section">
              <div className="gm-folder-section-header">
                <p>RESULTADOS TEST DE COMPETENCIAS</p>
                {
                  mustBeShowAnswersAction &&
                    <button
                      className="gm-folder-section-button"
                      onClick={onSeeAnswersClick}>Ver respuestas</button>
                }
              </div>
              <CompetencyTestBarCharts test={competencyTest} />
            </div>
        }
      </div>
    </div>
  );
};

const FolderContentComponent = (props) => (
  <>
    <CollaboratorInfo collaborator={props.collaborator} />
    <CollaboratorResults {...props} />
  </>
);

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const bar = payload[0].payload;
    return (
      <div className="custom-tooltip" style={{ background: "#fff", padding: "10px", borderRadius: "5px", boxShadow: "0px 0px 10px rgba(0,0,0,0.1)" }}>
        <p style={{ color: '#EE3F22' }}><strong>{bar.group}</strong></p>
        <p style={{ color: '#003849' }}>{`${metaForCompetency(bar.competency).label}:`} <strong>{bar.level}</strong></p>
      </div>
    );
  }
  return null;
};

const PERCENT_OF = (points) => Math.floor((points * 100) / 12);

/**
 * Una barra por competencia: cada competencia evaluada es su propia categoría
 * del eje X. Así el ancho que ocupa cada perfil es proporcional a cuántas
 * competencias tenga (recharts reparte el ancho por categoría), en vez de dar a
 * los cuatro colores el mismo slot y dejar huecos en los perfiles con pocas
 * barras. El rótulo del grupo se dibuja una sola vez y centrado con
 * `GroupAxisLabels`; el color de la barra ya identifica al perfil.
 *
 * `sortResultsByCatalog` deja las competencias en orden de catálogo, por lo que
 * las de un mismo color quedan contiguas. Las claves fuera del catálogo caen al
 * grupo fallback "Otras" al final, sin descartarse.
 */
export const buildCompetencyChartData = (filteredPointsTest) => {
  const result = sortResultsByCatalog(filteredPointsTest) || {};

  return Object.keys(result).map((key) => {
    const competency = metaForCompetency(key);
    return {
      competency: key,
      group: competency.group,
      color: competency.color,
      valor: PERCENT_OF(result[key].points),
      level: result[key].level,
    };
  });
};

/**
 * Agrupa las barras contiguas del mismo color en segmentos, para rotular cada
 * perfil una única vez centrado bajo sus barras. Devuelve, por segmento, la
 * primera y última competencia (para ubicar el centro) y cuántas barras abarca.
 */
export const buildGroupSegments = (data) => {
  const segments = [];

  data.forEach((bar) => {
    const last = segments[segments.length - 1];
    if (last && last.group === bar.group) {
      last.lastKey = bar.competency;
      last.count += 1;
    } else {
      segments.push({ group: bar.group, firstKey: bar.competency, lastKey: bar.competency, count: 1 });
    }
  });

  return segments;
};

/**
 * Capa SVG que rotula cada grupo DISC una vez, centrado horizontalmente entre su
 * primera y su última barra. Usa la escala de banda del eje X (inyectada por
 * recharts vía `Customized`) para ubicar el centro exacto, así el centrado es
 * correcto tenga el grupo 1, 2, 3 o 4 barras.
 */
const GroupAxisLabels = ({ xAxisMap, offset, segments }) => {
  if (!xAxisMap || !offset || !segments) return null;

  const axisId = Object.keys(xAxisMap)[0];
  const scale = xAxisMap[axisId]?.scale;
  if (!scale) return null;

  const bandwidth = typeof scale.bandwidth === 'function' ? scale.bandwidth() : 0;
  const centerOf = (key) => scale(key) + bandwidth / 2;
  const y = offset.top + offset.height + 18;

  return (
    <g>
      {segments.map((seg) => (
        <text
          key={`${seg.group}-${seg.firstKey}`}
          x={(centerOf(seg.firstKey) + centerOf(seg.lastKey)) / 2}
          y={y}
          textAnchor="middle"
          fill="#666"
          fontSize={13}
        >
          {seg.group}
        </text>
      ))}
    </g>
  );
};

const CompetencyTestBarCharts = ({ test }) => {
  const data = buildCompetencyChartData(test?.results?.filteredPointsTest);
  const segments = buildGroupSegments(data);

  return (
    <div className="chart-container">
      <div className="chart-flex-container">
        <div className="chart-graph-container">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} barCategoryGap="20%">
              <XAxis dataKey="competency" tick={false} height={30} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="valor" radius={[5, 5, 0, 0]} maxBarSize={50}>
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
              <Customized component={GroupAxisLabels} segments={segments} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default FolderContentComponent;
